import React, { useState } from "react";
import useSWR, { mutate } from "swr";
import api from "@/lib/api";
import { ddmmyyyy } from "@/lib/format";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle, DialogTrigger,
} from "@/components/ui/dialog";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import { UserPlus, KeyRound, Trash2, Loader2, Shield, Pencil, Save, RotateCcw } from "lucide-react";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";

const fetcher = (url) => api.get(url).then((r) => r.data);

const roleBadge = {
  platform_owner: "bg-brand-red/10 text-brand-red border-brand-red/20",
  company_admin: "bg-brand-navy/10 text-brand-navy border-brand-navy/20",
  depot_manager: "bg-blue-100 text-blue-800 border-blue-200",
  driver: "bg-emerald-100 text-emerald-700 border-emerald-200",
};

const roleLabel = (r) => (r || "").replace("_", " ");

const emptyUser = {
  email: "", full_name: "", role: "depot_manager", password: "",
  company_id: "", driver_id: "", must_change_password: true,
};

const Users = () => {
  const { user: me } = useAuth();
  const { data: users, isLoading } = useSWR("/users", fetcher);
  const { data: drivers } = useSWR("/drivers", fetcher);
  const { data: companies } = useSWR(me?.role === "platform_owner" ? "/companies" : null, fetcher);

  const [newOpen, setNewOpen] = useState(false);
  const [form, setForm] = useState(emptyUser);
  const [saving, setSaving] = useState(false);

  const [resetOpen, setResetOpen] = useState(null); // user_id when open
  const [resetPwd, setResetPwd] = useState("");

  // Edit dialog state
  const [editUser, setEditUser] = useState(null);
  const [editForm, setEditForm] = useState({});
  const [editing, setEditing] = useState(false);
  const openEdit = (u) => {
    setEditUser(u);
    setEditForm({
      email: u.email || "",
      full_name: u.full_name || "",
      role: u.role || "depot_manager",
      company_id: u.company_id || "",
      driver_id: u.driver_id || "",
      status: u.status || "active",
    });
  };
  const setE = (k, v) => setEditForm((f) => ({ ...f, [k]: v }));
  const submitEdit = async (e) => {
    e.preventDefault();
    if (!editForm.email || !editForm.full_name) {
      toast.error("Email and full name are required");
      return;
    }
    setEditing(true);
    try {
      const payload = { ...editForm };
      if (!isOwner) delete payload.company_id;
      if (payload.role !== "driver") delete payload.driver_id;
      Object.keys(payload).forEach((k) => { if (payload[k] === "") delete payload[k]; });
      await api.patch(`/users/${editUser.id}`, payload);
      toast.success(`${payload.full_name || editUser.full_name} updated`);
      setEditUser(null);
      mutate("/users");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Save failed");
    }
    setEditing(false);
  };

  const isOwner = me?.role === "platform_owner";

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.email || !form.full_name || !form.password) {
      toast.error("Email, full name and password are required");
      return;
    }
    if (form.password.length < 6) return toast.error("Password must be ≥ 6 chars");
    setSaving(true);
    try {
      const payload = { ...form };
      if (!isOwner) delete payload.company_id;
      if (payload.role !== "driver") delete payload.driver_id;
      Object.keys(payload).forEach((k) => { if (payload[k] === "") delete payload[k]; });
      await api.post("/users", payload);
      toast.success(`${payload.full_name} added as ${roleLabel(payload.role)}`);
      setNewOpen(false);
      setForm(emptyUser);
      mutate("/users");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Save failed");
    }
    setSaving(false);
  };

  const doReset = async (id) => {
    if (resetPwd.length < 6) return toast.error("Password must be ≥ 6 chars");
    try {
      await api.post(`/users/${id}/reset-password`, { new_password: resetPwd, must_change_password: true });
      toast.success("Password reset. User must change it at next login.");
      setResetOpen(null);
      setResetPwd("");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Reset failed");
    }
  };

  const doDeactivate = async (id, name) => {
    if (!window.confirm(`Deactivate ${name}? They will no longer be able to log in.`)) return;
    try {
      await api.delete(`/users/${id}?hard=false`);
      toast.success("User deactivated");
      mutate("/users");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed");
    }
  };

  const [deleting, setDeleting] = useState(null);
  const doHardDelete = async () => {
    if (!deleting) return;
    try {
      await api.delete(`/users/${deleting.id}?hard=true`);
      toast.success(`${deleting.full_name} permanently deleted`);
      setDeleting(null);
      mutate("/users");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Delete failed");
    }
  };
  const doReactivate = async (u) => {
    try {
      await api.post(`/users/${u.id}/reactivate`);
      toast.success(`${u.full_name} reactivated`);
      mutate("/users");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Reactivate failed");
    }
  };

  const toggleStatus = async (u) => {
    const next = u.status === "active" ? "inactive" : "active";
    try {
      await api.patch(`/users/${u.id}`, { status: next });
      toast.success(`User ${next}`);
      mutate("/users");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed");
    }
  };

  return (
    <div className="p-4 md:p-8 space-y-6">
      <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-3">
        <div>
          <div className="text-[10px] uppercase tracking-[0.3em] text-brand-red font-mono">Access · roles</div>
          <h1 className="font-heading text-3xl md:text-4xl font-bold tracking-tight text-brand-text mt-1">Users</h1>
          <p className="text-sm text-brand-text-muted mt-1">
            {users?.length || 0} account{(users?.length || 0) === 1 ? "" : "s"} · manage admins, managers and drivers.
          </p>
        </div>
        <Dialog open={newOpen} onOpenChange={setNewOpen}>
          <DialogTrigger asChild>
            <Button className="bg-brand-red hover:bg-brand-red-hover" data-testid="user-add-btn">
              <UserPlus className="w-4 h-4 mr-1.5" /> New user
            </Button>
          </DialogTrigger>
          <DialogContent className="max-w-lg">
            <DialogHeader>
              <DialogTitle>Add a new user</DialogTitle>
              <DialogDescription>They'll get sign-in access with the role you pick.</DialogDescription>
            </DialogHeader>
            <form onSubmit={submit} className="space-y-3">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                <div>
                  <Label className="text-xs uppercase tracking-wider text-brand-text-muted">Full name *</Label>
                  <Input required data-testid="user-name" value={form.full_name}
                    onChange={(e) => set("full_name", e.target.value)} />
                </div>
                <div>
                  <Label className="text-xs uppercase tracking-wider text-brand-text-muted">Email *</Label>
                  <Input required type="email" data-testid="user-email" value={form.email}
                    onChange={(e) => set("email", e.target.value)} autoCapitalize="off" />
                </div>
                <div>
                  <Label className="text-xs uppercase tracking-wider text-brand-text-muted">Role</Label>
                  <Select value={form.role} onValueChange={(v) => set("role", v)}>
                    <SelectTrigger data-testid="user-role"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      {isOwner && <SelectItem value="platform_owner">Platform Owner</SelectItem>}
                      <SelectItem value="company_admin">Company Admin</SelectItem>
                      <SelectItem value="depot_manager">Depot Manager</SelectItem>
                      <SelectItem value="driver">Driver</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label className="text-xs uppercase tracking-wider text-brand-text-muted">Temporary password *</Label>
                  <Input required data-testid="user-password" value={form.password}
                    onChange={(e) => set("password", e.target.value)} placeholder="≥ 6 characters" />
                </div>
                {isOwner && form.role !== "platform_owner" && (
                  <div className="md:col-span-2">
                    <Label className="text-xs uppercase tracking-wider text-brand-text-muted">Company</Label>
                    <Select value={form.company_id} onValueChange={(v) => set("company_id", v)}>
                      <SelectTrigger><SelectValue placeholder="Select company" /></SelectTrigger>
                      <SelectContent>
                        {(companies || []).map((c) => (
                          <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                )}
                {form.role === "driver" && (
                  <div className="md:col-span-2">
                    <Label className="text-xs uppercase tracking-wider text-brand-text-muted">Link to driver profile (optional)</Label>
                    <Select value={form.driver_id} onValueChange={(v) => set("driver_id", v)}>
                      <SelectTrigger><SelectValue placeholder="Not linked yet" /></SelectTrigger>
                      <SelectContent>
                        {(drivers || []).map((d) => (
                          <SelectItem key={d.id} value={d.id}>{d.full_name} ({d.driver_code})</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    <p className="text-[11px] text-brand-text-muted mt-1">
                      Link this account to an existing driver record so they see their own pay/work in the driver portal.
                    </p>
                  </div>
                )}
              </div>
              <DialogFooter>
                <Button type="submit" disabled={saving} className="bg-brand-red hover:bg-brand-red-hover" data-testid="user-save">
                  {saving ? <Loader2 className="w-4 h-4 mr-1.5 animate-spin" /> : <UserPlus className="w-4 h-4 mr-1.5" />}
                  Create user
                </Button>
              </DialogFooter>
            </form>
          </DialogContent>
        </Dialog>
      </div>

      {isLoading && <div className="text-sm text-brand-text-muted">Loading…</div>}

      <Card className="rounded-2xl border-brand-border overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm" data-testid="users-table">
            <thead className="bg-brand-surface text-brand-text-muted uppercase tracking-wider text-[10px]">
              <tr>
                <th className="text-left px-4 py-3">Name</th>
                <th className="text-left px-4 py-3">Email</th>
                <th className="text-left px-4 py-3">Role</th>
                <th className="text-left px-4 py-3">Status</th>
                <th className="text-left px-4 py-3">Added</th>
                <th className="text-right px-4 py-3">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-brand-border">
              {(users || []).map((u) => (
                <tr key={u.id} data-testid={`user-row-${u.id}`}>
                  <td className="px-4 py-2.5">
                    <div className="flex items-center gap-2">
                      <div className="w-8 h-8 rounded-lg bg-brand-navy text-white grid place-items-center text-[11px] font-heading font-bold">
                        {u.full_name.split(" ").map((x) => x[0]).slice(0, 2).join("")}
                      </div>
                      <div>
                        <div className="font-medium text-brand-text">{u.full_name}</div>
                        {u.must_change_password && (
                          <div className="text-[10px] text-amber-700">password reset required</div>
                        )}
                      </div>
                    </div>
                  </td>
                  <td className="px-4 py-2.5 text-xs font-mono text-brand-text-muted">{u.email}</td>
                  <td className="px-4 py-2.5">
                    <Badge variant="outline" className={`text-[10px] uppercase tracking-wider ${roleBadge[u.role] || ""}`}>
                      {roleLabel(u.role)}
                    </Badge>
                  </td>
                  <td className="px-4 py-2.5">
                    <button
                      onClick={() => toggleStatus(u)}
                      disabled={u.id === me?.id}
                      className={`text-[10px] uppercase tracking-wider px-2 py-0.5 rounded-full border ${
                        u.status === "active"
                          ? "bg-emerald-100 text-emerald-700 border-emerald-200"
                          : "bg-slate-100 text-brand-text-muted border-brand-border"
                      } ${u.id === me?.id ? "cursor-not-allowed opacity-60" : "cursor-pointer"}`}
                      title={u.id === me?.id ? "You can't change your own status" : "Click to toggle"}
                    >
                      {u.status}
                    </button>
                  </td>
                  <td className="px-4 py-2.5 font-mono text-xs">{ddmmyyyy(u.created_at)}</td>
                  <td className="px-4 py-2.5 text-right">
                    <div className="inline-flex gap-1">
                      <Button size="sm" variant="ghost" onClick={() => openEdit(u)} data-testid={`user-edit-${u.id}`}>
                        <Pencil className="w-3.5 h-3.5 text-brand-text-muted" />
                      </Button>
                      <Button size="sm" variant="ghost" onClick={() => { setResetOpen(u.id); setResetPwd(""); }} data-testid={`user-reset-${u.id}`}>
                        <KeyRound className="w-3.5 h-3.5 text-brand-text-muted" />
                      </Button>
                      {u.status === "inactive" && (
                        <Button size="sm" variant="ghost" onClick={() => doReactivate(u)} data-testid={`user-reactivate-${u.id}`} title="Reactivate">
                          <RotateCcw className="w-3.5 h-3.5 text-emerald-600" />
                        </Button>
                      )}
                      {u.id !== me?.id && (
                        <Button size="sm" variant="ghost" onClick={() => setDeleting(u)} data-testid={`user-del-${u.id}`}>
                          <Trash2 className="w-3.5 h-3.5 text-red-600" />
                        </Button>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
              {!isLoading && (users || []).length === 0 && (
                <tr><td colSpan="6" className="text-center py-12 text-brand-text-muted text-sm">
                  <Shield className="w-6 h-6 mx-auto mb-2 opacity-40" /> No users yet.
                </td></tr>
              )}
            </tbody>
          </table>
        </div>
      </Card>

      <Dialog open={!!editUser} onOpenChange={(v) => !v && setEditUser(null)}>
        <DialogContent className="max-w-lg" data-testid="user-edit-dialog">
          <DialogHeader>
            <DialogTitle>Edit {editUser?.full_name}</DialogTitle>
            <DialogDescription>Update the user's details. Use "Reset password" for password changes.</DialogDescription>
          </DialogHeader>
          <form onSubmit={submitEdit} className="space-y-3">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              <div>
                <Label className="text-xs uppercase tracking-wider text-brand-text-muted">Full name *</Label>
                <Input required data-testid="user-edit-name" value={editForm.full_name || ""}
                       onChange={(e) => setE("full_name", e.target.value)} />
              </div>
              <div>
                <Label className="text-xs uppercase tracking-wider text-brand-text-muted">Email *</Label>
                <Input required type="email" data-testid="user-edit-email" value={editForm.email || ""}
                       onChange={(e) => setE("email", e.target.value)} autoCapitalize="off" />
              </div>
              <div>
                <Label className="text-xs uppercase tracking-wider text-brand-text-muted">Role</Label>
                <Select value={editForm.role} onValueChange={(v) => setE("role", v)}
                        disabled={editUser?.id === me?.id}>
                  <SelectTrigger data-testid="user-edit-role"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {isOwner && <SelectItem value="platform_owner">Platform Owner</SelectItem>}
                    <SelectItem value="company_admin">Company Admin</SelectItem>
                    <SelectItem value="depot_manager">Depot Manager</SelectItem>
                    <SelectItem value="driver">Driver</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className="text-xs uppercase tracking-wider text-brand-text-muted">Status</Label>
                <Select value={editForm.status} onValueChange={(v) => setE("status", v)}>
                  <SelectTrigger data-testid="user-edit-status"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="active">Active</SelectItem>
                    <SelectItem value="inactive">Inactive</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              {isOwner && editForm.role !== "platform_owner" && (
                <div className="md:col-span-2">
                  <Label className="text-xs uppercase tracking-wider text-brand-text-muted">Company</Label>
                  <Select value={editForm.company_id || ""} onValueChange={(v) => setE("company_id", v)}>
                    <SelectTrigger><SelectValue placeholder="Select company" /></SelectTrigger>
                    <SelectContent>
                      {(companies || []).map((c) => (
                        <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              )}
              {editForm.role === "driver" && (
                <div className="md:col-span-2">
                  <Label className="text-xs uppercase tracking-wider text-brand-text-muted">Link to driver profile</Label>
                  <Select value={editForm.driver_id || ""} onValueChange={(v) => setE("driver_id", v)}>
                    <SelectTrigger><SelectValue placeholder="Not linked" /></SelectTrigger>
                    <SelectContent>
                      {(drivers || []).map((d) => (
                        <SelectItem key={d.id} value={d.id}>{d.full_name} ({d.driver_code})</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              )}
            </div>
            <DialogFooter>
              <Button type="submit" disabled={editing} className="bg-brand-red hover:bg-brand-red-hover" data-testid="user-edit-save">
                {editing ? <Loader2 className="w-4 h-4 mr-1.5 animate-spin" /> : <Save className="w-4 h-4 mr-1.5" />}
                Save changes
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      <Dialog open={!!resetOpen} onOpenChange={(v) => !v && setResetOpen(null)}>
        <DialogContent className="max-w-sm">
          <DialogHeader>
            <DialogTitle>Reset password</DialogTitle>
            <DialogDescription>The user must change this at next login.</DialogDescription>
          </DialogHeader>
          <div>
            <Label className="text-xs uppercase tracking-wider text-brand-text-muted">New password</Label>
            <Input value={resetPwd} onChange={(e) => setResetPwd(e.target.value)} placeholder="≥ 6 characters" data-testid="reset-pwd-input" />
          </div>
          <DialogFooter>
            <Button className="bg-brand-red hover:bg-brand-red-hover" onClick={() => doReset(resetOpen)} data-testid="reset-pwd-save">
              <KeyRound className="w-4 h-4 mr-1.5" /> Reset
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <AlertDialog open={!!deleting} onOpenChange={(v) => !v && setDeleting(null)}>
        <AlertDialogContent data-testid="user-delete-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>Permanently delete {deleting?.full_name}?</AlertDialogTitle>
            <AlertDialogDescription>
              This will permanently remove the user account. This cannot be undone. If they had a linked driver profile, that profile is <b>not</b> deleted — only their login access.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="user-delete-cancel">Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={doHardDelete} className="bg-red-600 hover:bg-red-700"
                                data-testid="user-delete-confirm">Permanently delete</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};

export default Users;
